import getSepaEnabledBankCountries from '/shared/functions/getSepaEnabledBankCountries';

interface LegacyWindow extends Window {
  MSInputMethodContext?: object;
}

interface LegacyDocument extends Document {
  documentMode?: boolean | number;
}

interface Target {
  isConsumer: boolean;
  isBusiness: boolean;
  isCMPMain: boolean;
  isAirbp: boolean;
  value?: string;
  hotjarUid?: string;
}

export const inputRegex = /^[A-Za-z0-9 \-!?"'&*()/@$.,%:]+$/gi;
export const emailRegex =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;
export const numberSpacesRegex = /^[0-9\s+()]+$/gi;
export const apiEndpoint = (import.meta.env.api_url || 'http://localhost:3000').replace(/\/$/, '');
export const assetsUrl = import.meta.env.api_asset_url || `${apiEndpoint}/assets`;
export const businessUrl = (import.meta.env.business_url || 'http://localhost:7979').replace(/\/$/, '');
export const idpUrl = (import.meta.env.idp_url || 'https://stg-energyid.bpglobal.com').replace(/\/$/, '');
export const hotjarBusinessUid = (import.meta.env.hotjar_business_uid || '2737458').replace(/\/$/, '');

export const googleTag4TrackingCode = import.meta.env.GA_TAG_4_TRACKING_ID || false;
export const sentrySixAgencyDebuggingEnabled = import.meta.env.ENABLE_SENTRY
  ? import.meta.env.ENABLE_SENTRY === 'true'
  : false;
export const sentrySixAgencyInjestionUrl = import.meta.env.SENTRY_INJESTION_URL || '';

export const authCheckerTimeout = import.meta.env.auth_checker_timeout || 60000;
export const isTest = ['test', 'ci'].indexOf(import.meta.env.NODE_ENV) > -1;
export const isMultilingualEnabled = import.meta.env.multilingual_enabled
  ? import.meta.env.multilingual_enabled === 'true'
  : false;
export const cookieBaseDomain = (import.meta.env.cookie_domain || window.location.hostname || '.bp.com').replace(
  /\/$/,
  '',
);
export const sessionTTL = import.meta.env.session_ttl || 1440;

export const blankAxiosOpts = { withCredentials: false, headers: null };

export const consumerFakeLoginFlag = import.meta.env.consumer_enable_fake_login
  ? import.meta.env.consumer_enable_fake_login === 'true'
  : false;
export const businessFakeLoginFlag = import.meta.env.business_enable_fake_login
  ? import.meta.env.business_enable_fake_login === 'true'
  : false;

export const isDevelopmentModeFlag = import.meta.env.IS_DEVELOPMENT_MODE
  ? import.meta.env.IS_DEVELOPMENT_MODE === 'true'
  : false;

export const reduxDevLogging = import.meta.env.redux_dev_logging === 'true';

// We need to stop logger being loaded within jest tests (which is pretty fiddly)
// also we need to stop things such as debugBar showing within snapshots
export const developmentMode =
  (import.meta.env.NODE_ENV === 'development' || isDevelopmentModeFlag) && import.meta.env.JEST_WORKER_ID === undefined;

export const stripeBusinessPublishableKey = import.meta.env.STRIPE_PUBLISHABLE_KEY || false;
export const stripeBusinessEuroPublishableKey = import.meta.env.STRIPE_EURO_PUBLISHABLE_KEY || false;
export const contexts = {
  CONSUMER: 'consumer',
  BUSINESS: 'business',
  CASTROL: 'castrol',
  CMPDEFAULT: 'cmpdefault',
  AIRBP: 'airbp',
};

export const locales = {
  CHINESE: 'zh',
  ENGLISH: 'en',
  TEST: 'xx',
};

export const currencyTypeKeys = {
  BUSINESS_SUGGEST: 'b2b-suggest',
  BUSINESS_FILTER: 'b2b-filter',
  CONSUMER_PAY: 'b2c-pay',
  CONSUMER_DISPLAY: 'b2c-display',
} as const;

export const target = (() => {
  const output: Target = {
    isConsumer: false,
    isBusiness: false,
    isCMPMain: false,
    isAirbp: false,
  };
  const envTarget = import.meta.env.target || import.meta.env.TARGET;
  switch (envTarget) {
    case contexts.AIRBP:
      output.isAirbp = true;
      output.isBusiness = true;
      output.value = contexts.AIRBP;
      output.hotjarUid = hotjarBusinessUid;
      break;
    // case contexts.CMPDEFAULT:
    default:
      output.isCMPMain = true;
      output.isBusiness = true;
      output.value = contexts.CMPDEFAULT;
      output.hotjarUid = hotjarBusinessUid;
      break;
  }
  return output;
})();
export const isIE11 = !!(window as LegacyWindow).MSInputMethodContext && !!(document as LegacyDocument).documentMode;
export const isTestRunner = import.meta.env.VITEST === 'true';

export const GB = 'gb';
export const bankCountriesStripe = [
  'gb', // uk
  'cn', // china
].concat(getSepaEnabledBankCountries());

export const QUESTION_BRANCH_ACTIVITY = 'activity';
export const QUESTION_BRANCH_CUSTOM = 'custom';
export const QUESTION_BRANCH_SUPPLIER = 'supplier';

export const QUESTION_BRANCH_NAMES = [
  QUESTION_BRANCH_ACTIVITY,
  QUESTION_BRANCH_CUSTOM,
  QUESTION_BRANCH_SUPPLIER,
] as const;
export type QuestionBranchName = (typeof QUESTION_BRANCH_NAMES)[number];

export const isQuestionBranchName = (value: string | null | undefined): value is QuestionBranchName =>
  Boolean(value && (QUESTION_BRANCH_NAMES as readonly string[]).includes(value));
